import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConnectedPosition, Overlay, OverlayModule } from '@angular/cdk/overlay';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { InfiniteScrollListItemDirective, DynamicVirtualViewportHeightDirective, TooltipModule } from '@libs/ui-lib';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { isNotNull } from '@libs/utils';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { TranslateModule } from '@ngx-translate/core';
import { AppointmentReason, AppointmentReasonList } from '@libs/store';
import { SvgIconComponent, SvgIcons } from '@ngneat/svg-icon';

const MINIMUM_SEARCH_LENGTH = 3;
const DEBOUNCE_TIME = 300;
const DEFAULT_HEIGHT = 444;

@Component({
  selector: 'lib-select-reason',
  imports: [
    CommonModule,
    LottieComponent,
    ReactiveFormsModule,
    OverlayModule,
    ScrollingModule,
    DynamicVirtualViewportHeightDirective,
    TranslateModule,
    InfiniteScrollListItemDirective,
    SvgIconComponent,
    TooltipModule,
  ],
  templateUrl: './select-reason.component.html',
  styleUrl: './select-reason.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectReasonComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly overlay = inject(Overlay);
  private readonly cdr = inject(ChangeDetectorRef);

  public readonly dropdownItems = input.required<AppointmentReasonList>();
  public readonly maxHeight = input<number>(DEFAULT_HEIGHT);
  public readonly prefixIcon = input<SvgIcons | null>(null);
  public readonly reasonsLoading = input<boolean>(false);
  public readonly loading = input<boolean>(false);
  public readonly noResultLabel = input<string>('');
  public readonly defaultValue = input<AppointmentReasonList | undefined>();
  public readonly minimumSearchLength = input<number>(MINIMUM_SEARCH_LENGTH);
  public readonly required = input<boolean>(true);
  public readonly submitAttempted = input<boolean>(false);

  public readonly itemSelected = output<AppointmentReason>();
  public readonly itemDeselected = output<AppointmentReason>();

  public readonly innerControl = new FormControl<string>('');
  public readonly isInputFocused = signal(false);
  public readonly dropdownHeight = signal(DEFAULT_HEIGHT);
  public readonly dropdownOpen = signal(false);
  public readonly isFocused = computed(() => this.isInputFocused() || this.dropdownOpen());
  public isInvalidReason = computed(
    () => this.required() && this.submitAttempted() && (!this.defaultValue() || this.defaultValue()?.length === 0),
  );

  public readonly searchTerm = signal('');
  public readonly selectedItems = computed(() => this.defaultValue() ?? null);

  public readonly filteredItems = computed<AppointmentReasonList>(
    () =>
      this.dropdownItems()?.filter(
        (item) =>
          item.code.toLowerCase().includes(this.searchTerm().toLowerCase()) ||
          item.abbreviation.toLowerCase().includes(this.searchTerm().toLowerCase()) ||
          item.name.toLowerCase().includes(this.searchTerm().toLowerCase()),
      ) ?? [],
  );

  public readonly scrollStrategy = this.overlay.scrollStrategies.noop();

  public readonly overlayPositionStrategy: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
  ];

  public lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  @ViewChild('dropdownControl', { static: true, read: ElementRef }) public dropdownControlElement!: ElementRef<HTMLDivElement>;
  @ViewChild('input') public inputElement!: ElementRef<HTMLInputElement>;

  constructor() {
    effect(() => {
      const searchTerm = this.searchTerm();

      if (searchTerm.length < this.minimumSearchLength() && this.dropdownOpen()) {
        this.closeDropdown();
      }
    });
  }

  public ngOnInit(): void {
    this.innerControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(DEBOUNCE_TIME), filter(isNotNull), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.searchTerm.set(searchTerm);

        this.openDropdown();
      });
  }

  public ngAfterViewInit(): void {
    this.inputElement.nativeElement.focus();
  }

  public onOverlayOutsideClick(event: MouseEvent): void {
    const clickTarget = event.target as HTMLElement;
    const isFilterCloseButtonClicked = clickTarget.closest('.dropdown-button');
    const isInputFieldClicked = this.dropdownControlElement.nativeElement.contains(clickTarget);

    if (isFilterCloseButtonClicked || isInputFieldClicked) {
      return;
    }
    event.preventDefault();
    this.closeDropdown();
  }

  public getDropdownContainerHeight(): number {
    const rect = this.dropdownControlElement.nativeElement.getBoundingClientRect().bottom;
    const windowHeight = window.innerHeight;
    return windowHeight - rect;
  }

  public setDropdownHeight(height: number): void {
    this.dropdownHeight.set(height);
    this.cdr.markForCheck();
  }

  public openDropdown(): void {
    if (
      this.dropdownOpen() ||
      !this.innerControl.value ||
      (this.innerControl.value && this.innerControl.value?.length < MINIMUM_SEARCH_LENGTH)
    ) {
      return;
    }
    this.setDropdownHeight(this.getDropdownContainerHeight());
    this.dropdownOpen.set(true);
  }

  public closeDropdown(): void {
    this.dropdownOpen.set(false);
  }

  public selectItem(item: AppointmentReason): void {
    this.itemSelected.emit(item);
    this.innerControl.setValue('');
    this.closeDropdown();
    this.inputElement.nativeElement.focus();
  }

  public deselectItem(item: AppointmentReason): void {
    this.itemDeselected.emit(item);
    this.closeDropdown();
    this.isInputFocused.set(false);
  }

  public isItemSelected(item: AppointmentReason): boolean {
    return !!this.selectedItems()?.find((selectedItem) => selectedItem.progressNoteId == null && selectedItem.code === item.code);
  }

  public onFocus(): void {
    this.isInputFocused.set(true);
  }

  public onBlur(): void {
    this.isInputFocused.set(false);
  }
}
